import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, ArrowRight, BadgeCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

const SLIDES = [
  { src: "/images/hero-laser.webp", alt: "Appareil d'épilation laser financé par Solubail", caption: "Épilation laser" },
  { src: "/images/hero.webp", alt: "Loader Volvo L70H financé par Solubail", caption: "Loader Volvo L70H" },
  { src: "/images/hero-nacelle.webp", alt: "Camion nacelle International financé par Solubail", caption: "Nacelle International" },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const METRICS = [
  { value: "24–48 h", label: "délai moyen d'approbation" },
];

const Hero = () => {
  const [slide, setSlide] = useState(0);
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: frameRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const onTilt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section id="accueil" data-testid="hero-section" ref={frameRef} className="relative overflow-hidden pt-[72px]">
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <div className="absolute inset-0 opacity-[0.06] bg-[linear-gradient(rgba(10,18,32,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(10,18,32,0.35)_1px,transparent_1px)] bg-[size:72px_72px]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 lg:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-400/10 px-4 py-1.5"
          >
            <BadgeCheck className="w-4 h-4 text-sky-600" />
            <span data-testid="hero-trust-badge" className="font-mono text-xs uppercase tracking-[0.2em] text-sky-600">
              Courtier au Québec — Réponse en 24 à 48 h
            </span>
          </motion.div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.92] tracking-tight text-slate-900 mt-7">
            <MaskedLine delay={0.25}>Financez vos</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-sky-600">équipements</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>sans délais bancaires</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="text-base sm:text-lg text-slate-500 leading-relaxed mt-6 max-w-xl"
          >
            Crédit-bail, refinancement et fonds de roulement sur mesure pour les PME
            québécoises : camions, excavatrices, équipements médicaux, agricoles et plus.
            Vous choisissez l'équipement — on s'occupe du financement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="flex flex-col sm:flex-row gap-4 mt-9"
          >
            <button
              data-testid="hero-quote-cta-button"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 px-8 py-4 text-base font-bold text-[#0A1220] hover:bg-sky-300 transition-colors duration-300"
            >
              Vérifiez votre admissibilité
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-call-button"
              href={PHONE_TEL}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-900/20 px-8 py-4 text-base font-semibold text-slate-900 hover:border-sky-400 hover:text-sky-600 transition-colors duration-300"
            >
              <Phone className="w-5 h-5" />
              Appeler le {PHONE_DISPLAY}
            </a>
          </motion.div>

          <div className="grid grid-cols-2 gap-6 mt-12 pt-8 border-t border-slate-900/10 max-w-md">
            {METRICS.map((m, i) => (
              <motion.div
                key={m.label}
                data-testid={`hero-metric-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + i * 0.12 }}
              >
                <p className="font-display text-3xl font-extrabold text-slate-900">{m.value}</p>
                <p className="font-mono text-[11px] uppercase tracking-wider text-slate-9000 mt-1">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
          style={{ perspective: 1000 }}
        >
          <motion.div
            data-testid="hero-image-frame"
            onMouseMove={onTilt}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-sky-400/30 clip-frame" aria-hidden="true" />
            <div className="clip-frame overflow-hidden relative h-[540px]">
              <AnimatePresence mode="sync">
                <motion.img
                  key={slide}
                  src={SLIDES[slide].src}
                  alt={SLIDES[slide].alt}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1.15 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  style={{ y: parallaxY }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1220]/70 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-5 right-5 flex gap-2 z-10">
                {SLIDES.map((s, i) => (
                  <button
                    key={s.src}
                    data-testid={`hero-slide-dot-${i}`}
                    onClick={() => setSlide(i)}
                    aria-label={`Voir : ${s.caption}`}
                    className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${i === slide ? "bg-sky-400" : "bg-white/60 hover:bg-white"}`}
                  />
                ))}
              </div>
            </div>
            <div className="absolute bottom-6 left-6 glass rounded-xl px-5 py-4" style={{ transform: "translateZ(40px)" }}>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-600">Dossiers récents</p>
              <AnimatePresence mode="wait">
                <motion.p
                  key={slide}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.35 }}
                  className="text-sm font-semibold text-slate-900 mt-1"
                >
                  {SLIDES[slide].caption}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
